import { PlatformError, type DocTxOps } from '@djgg/core';
import { RuntimeReleaseBlockedError, assertRuntimeAdmission,
  type RuntimeAdmissionControlsV1, type RuntimeExecutionRevocationsV1 } from './compositionRuntimeRelease.js';
import type { AcceptedCompositionV1 } from './spec/gameSpec.js';
import { compositionRuntimeControlDoc } from './paths.js';
import { validateCompositionActiveRuntimeV1, type CompositionActiveRuntimeV1 } from './compositionActiveRuntime.js';

const SHA256 = /^[a-f0-9]{64}$/;
const CONTROL_REVISION = /^composition-control@([1-9][0-9]{0,14})$/;
const COMMAND_ID = /^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/;
const PROFILE = /^[a-z0-9][a-z0-9._@/-]{0,127}$/;
const SUBJECT = /^[^\s\x00-\x1f\x7f][^\x00-\x1f\x7f]{0,127}$/;
const ACTOR = /^[A-Za-z0-9][A-Za-z0-9._:@/ -]{0,255}$/;

export interface CompositionRuntimeControlV2 {
  schemaVersion: 2;
  revision: number;
  commandId: string;
  /** Immutable private descriptor selected for new admission and retained
   * execution. Null is the deliberate unconfigured cutover state. */
  activeRuntime: CompositionActiveRuntimeV1 | null;
  admissionControls: RuntimeAdmissionControlsV1;
  /** Missing historical rows are the closed cohort mode. Broad admission is
   * possible only through this explicit durable value. */
  admissionAudience?: 'cohort' | 'all-creators';
  cohortSubjectUids: string[];
  /** Directly consumable by the released-runtime execution boundary. */
  executionRevocations: RuntimeExecutionRevocationsV1;
  reason: string;
  updatedAt: number;
  updatedBy: string;
}

export interface CompositionAdmissionFenceV1 {
  revision: number;
  subjectUid: string;
}

export interface CompositionAdmissionFenceV2 extends CompositionAdmissionFenceV1 {
  profile: string;
}

export interface LockedCompositionRuntimeControlV2 {
  stored: unknown | null;
}

function plainObject(value: unknown, label: string): Record<string, unknown> {
  if (value === null || typeof value !== 'object' || Array.isArray(value)
    || Object.getPrototypeOf(value) !== Object.prototype) {
    throw new PlatformError('validation', `invalid ${label}`);
  }
  return value as Record<string, unknown>;
}

function exactKeys(value: Record<string, unknown>, keys: string[], label: string): void {
  const actual = Object.keys(value).sort();
  const expected = [...keys].sort();
  if (actual.length !== expected.length || actual.some((key, index) => key !== expected[index])) {
    throw new PlatformError('validation', `invalid ${label} fields`);
  }
}

function stringList(value: unknown, label: string, pattern: RegExp, limit: number): string[] {
  if (!Array.isArray(value) || value.length > limit) throw new PlatformError('validation', `invalid ${label}`);
  const result = value.map((entry) => {
    if (typeof entry !== 'string' || !pattern.test(entry) || entry.trim() !== entry) {
      throw new PlatformError('validation', `invalid ${label}`);
    }
    return entry;
  });
  if (new Set(result).size !== result.length) throw new PlatformError('validation', `duplicate ${label}`);
  return result;
}

export function compositionControlRevision(revision: number): string {
  if (!Number.isSafeInteger(revision) || revision < 1) {
    throw new PlatformError('validation', 'composition control revision must be positive');
  }
  return `composition-control@${revision}`;
}

/** Strict server-owned control parser. Callers must fail closed on any error. */
export function validateCompositionRuntimeControlV2(value: unknown): CompositionRuntimeControlV2 {
  const raw = plainObject(value, 'composition runtime control');
  exactKeys(raw, [
    'schemaVersion', 'revision', 'commandId', 'activeRuntime', 'admissionControls',
    ...(Object.hasOwn(raw, 'admissionAudience') ? ['admissionAudience'] : []), 'cohortSubjectUids',
    'executionRevocations', 'reason', 'updatedAt', 'updatedBy',
  ], 'composition runtime control');
  if (raw.schemaVersion !== 2) throw new PlatformError('validation', 'unsupported composition runtime control schema');
  if (!Number.isSafeInteger(raw.revision) || (raw.revision as number) < 1) {
    throw new PlatformError('validation', 'composition control revision must be positive');
  }
  const revision = raw.revision as number;
  if (typeof raw.commandId !== 'string' || !COMMAND_ID.test(raw.commandId)) {
    throw new PlatformError('validation', 'composition control command id is invalid');
  }
  const activeRuntime = raw.activeRuntime === null ? null : validateCompositionActiveRuntimeV1(raw.activeRuntime);
  const admission = plainObject(raw.admissionControls, 'composition admission controls');
  exactKeys(admission, ['enabled', 'profiles', 'deniedReleaseIds'], 'composition admission controls');
  if (typeof admission.enabled !== 'boolean') throw new PlatformError('validation', 'invalid composition admission enabled flag');
  const admissionControls: RuntimeAdmissionControlsV1 = {
    enabled: admission.enabled,
    profiles: stringList(admission.profiles, 'composition admission profiles', PROFILE, 64),
    deniedReleaseIds: stringList(admission.deniedReleaseIds, 'composition admission release ids', SHA256, 64),
  };
  if (raw.admissionAudience !== undefined
    && raw.admissionAudience !== 'cohort' && raw.admissionAudience !== 'all-creators') {
    throw new PlatformError('validation', 'invalid composition admission audience');
  }
  const cohortSubjectUids = stringList(raw.cohortSubjectUids, 'composition cohort subjects', SUBJECT, 10_000);
  const execution = plainObject(raw.executionRevocations, 'composition execution revocations');
  exactKeys(execution, ['revision', 'releaseIds', 'closureDigests'], 'composition execution revocations');
  if (execution.revision !== compositionControlRevision(revision)
    || typeof execution.revision !== 'string' || !CONTROL_REVISION.test(execution.revision)) {
    throw new PlatformError('validation', 'composition execution revision does not match its control row');
  }
  const executionRevocations: RuntimeExecutionRevocationsV1 = {
    revision: execution.revision,
    releaseIds: stringList(execution.releaseIds, 'revoked composition releases', SHA256, 64),
    closureDigests: stringList(execution.closureDigests, 'revoked composition closures', SHA256, 64),
  };
  if (typeof raw.reason !== 'string' || raw.reason.length > 500) {
    throw new PlatformError('validation', 'composition control reason is invalid');
  }
  if (!Number.isSafeInteger(raw.updatedAt) || (raw.updatedAt as number) < 1) {
    throw new PlatformError('validation', 'composition control timestamp is invalid');
  }
  if (typeof raw.updatedBy !== 'string' || !ACTOR.test(raw.updatedBy)) {
    throw new PlatformError('validation', 'composition control actor is invalid');
  }
  return structuredClone({
    schemaVersion: 2,
    revision,
    commandId: raw.commandId,
    activeRuntime,
    admissionControls,
    ...(raw.admissionAudience === undefined ? {} : { admissionAudience: raw.admissionAudience }),
    cohortSubjectUids,
    executionRevocations,
    reason: raw.reason,
    updatedAt: raw.updatedAt as number,
    updatedBy: raw.updatedBy,
  });
}

export function compositionAdmissionAudienceV1(
  control: Pick<CompositionRuntimeControlV2, 'admissionAudience'>,
): 'cohort' | 'all-creators' {
  return control.admissionAudience ?? 'cohort';
}

function withoutId(value: unknown): unknown {
  const raw = plainObject(value, 'composition runtime control');
  const { id: _id, ...control } = raw;
  return control;
}

/** Lock the singleton before any job/creation/version lock. Policy validation
 * may be deferred until after an in-transaction exact-replay decision. */
export async function lockCompositionAdmissionControlV1(
  tx: DocTxOps,
  authority: AcceptedCompositionV1,
): Promise<LockedCompositionRuntimeControlV2 | undefined> {
  if (!authority.runtimeReleaseId) return undefined;
  return { stored: await tx.get<CompositionRuntimeControlV2>(compositionRuntimeControlDoc()) };
}

export function assertLockedCompositionAdmissionFenceV1(
  locked: LockedCompositionRuntimeControlV2 | undefined,
  input: {
    authority: AcceptedCompositionV1;
    fence?: CompositionAdmissionFenceV1;
    subjectUid: string;
  },
): CompositionRuntimeControlV2 | undefined {
  if (!input.authority.runtimeReleaseId) return undefined;
  if (!input.fence || input.fence.subjectUid !== input.subjectUid
    || !Number.isSafeInteger(input.fence.revision) || input.fence.revision < 1) {
    throw new RuntimeReleaseBlockedError(
      'runtime-admission-fence-required',
      'Released composition admission requires its exact durable control fence',
    );
  }
  const stored = locked?.stored;
  if (!stored) throw new RuntimeReleaseBlockedError(
    'runtime-admission-control-unavailable',
    'Composition runtime controls are unavailable',
  );
  let control: CompositionRuntimeControlV2;
  try {
    control = validateCompositionRuntimeControlV2(withoutId(stored));
  } catch {
    throw new RuntimeReleaseBlockedError(
      'runtime-admission-control-invalid',
      'Composition runtime controls are invalid',
    );
  }
  if (control.revision !== input.fence.revision) {
    throw new RuntimeReleaseBlockedError(
      'runtime-admission-control-changed',
      'Composition runtime controls changed before admission committed',
      control.executionRevocations.revision,
    );
  }
  if (compositionAdmissionAudienceV1(control) === 'cohort'
    && !control.cohortSubjectUids.includes(input.subjectUid)) {
    throw new RuntimeReleaseBlockedError(
      'runtime-admission-cohort-denied',
      'Authenticated subject is outside the released composition cohort',
      control.executionRevocations.revision,
    );
  }
  assertRuntimeAdmission({
    profile: input.authority.spec.profile,
    releaseId: input.authority.runtimeReleaseId,
    controls: control.admissionControls,
  });
  return control;
}


/** Locks and immediately validates the same singleton row that disables admission. */
export async function assertCompositionAdmissionFenceV1(
  tx: DocTxOps,
  input: {
    authority: AcceptedCompositionV1;
    fence?: CompositionAdmissionFenceV1;
    subjectUid: string;
  },
): Promise<CompositionRuntimeControlV2 | undefined> {
  const locked = await lockCompositionAdmissionControlV1(tx, input.authority);
  return assertLockedCompositionAdmissionFenceV1(locked, input);
}

/** Typed admission for the private V2 route. V2 has no V1 spec profile or
 * runtimeReleaseId field, so callers must supply a server-authenticated
 * maintained profile and the exact durable release/closure identities. */
export async function assertCompositionAdmissionFenceV2(
  tx: DocTxOps,
  input: {
    fence: CompositionAdmissionFenceV2;
    subjectUid: string;
    releaseId: string;
    closureDigest: string;
  },
  locked?: LockedCompositionRuntimeControlV2,
): Promise<CompositionRuntimeControlV2> {
  if (input.fence.subjectUid !== input.subjectUid
    || !Number.isSafeInteger(input.fence.revision) || input.fence.revision < 1
    || !PROFILE.test(input.fence.profile) || !SHA256.test(input.releaseId)
    || !SHA256.test(input.closureDigest)) {
    throw new RuntimeReleaseBlockedError(
      'runtime-admission-fence-required',
      'Private V2 admission requires its exact durable control fence',
    );
  }
  const stored = locked ? locked.stored
    : await tx.get<CompositionRuntimeControlV2>(compositionRuntimeControlDoc());
  if (!stored) throw new RuntimeReleaseBlockedError(
    'runtime-admission-control-unavailable',
    'Composition runtime controls are unavailable',
  );
  let control: CompositionRuntimeControlV2;
  try {
    control = validateCompositionRuntimeControlV2(withoutId(stored));
  } catch {
    throw new RuntimeReleaseBlockedError(
      'runtime-admission-control-invalid',
      'Composition runtime controls are invalid',
    );
  }
  if (control.revision !== input.fence.revision) throw new RuntimeReleaseBlockedError(
    'runtime-admission-control-changed',
    'Composition runtime controls changed before admission committed',
    control.executionRevocations.revision,
  );
  if (compositionAdmissionAudienceV1(control) === 'cohort'
    && !control.cohortSubjectUids.includes(input.subjectUid)) {
    throw new RuntimeReleaseBlockedError(
      'runtime-admission-cohort-denied',
      'Authenticated subject is outside the released composition cohort',
      control.executionRevocations.revision,
    );
  }
  assertRuntimeAdmission({ profile: input.fence.profile, releaseId: input.releaseId,
    controls: control.admissionControls });
  if (control.executionRevocations.releaseIds.includes(input.releaseId)
    || control.executionRevocations.closureDigests.includes(input.closureDigest)) {
    throw new RuntimeReleaseBlockedError(
      'runtime-execution-revoked',
      'Private V2 release or closure is execution-revoked',
      control.executionRevocations.revision,
    );
  }
  return control;
}
